import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  Chip,
  Popover,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { CheckCircle, HelpOutline } from '@mui/icons-material';
import { CurrentSet, UserProgress, WorkoutDay, RPE } from '../types/workout';
import { WorkoutUtils } from '../utils/workoutUtils';
import { getRPEDescription, formatRPEForDisplay } from '../utils/rpeUtils';
import { Exercise } from '../types/workout';

interface SetTrackerProps {
  sets: CurrentSet[];
  onUpdateSet: (setIndex: number, weight: number, reps: number, actualRpe?: RPE) => void;
  targetReps: string;
  exercise: Exercise;
  userProgress: UserProgress | null;
  currentWeek: number;
  currentDay: WorkoutDay;
}

// Calculator function to evaluate mathematical expressions
const calculateExpression = (expression: string): number | null => {
  try {
    // Remove any whitespace
    const cleanExpression = expression.replace(/\s/g, '');
    
    // Only allow numbers, +, -, *, /, (, ), and decimal points
    if (!/^[0-9+\-*/().]+$/.test(cleanExpression)) {
      return null;
    }
    
    // Prevent dangerous expressions (no eval for security)
    // Use Function constructor as a safer alternative to eval
    const result = Function(`"use strict"; return (${cleanExpression})`)();
    
    // Check if result is a valid number
    if (typeof result === 'number' && !isNaN(result) && isFinite(result) && result >= 0) {
      return Math.round(result * 100) / 100; // Round to 2 decimal places
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

// Convert kg to lbs
const convertKgToLbs = (kg: number): number => {
  return Math.round(kg * 2.20462 * 100) / 100; // Round to 2 decimal places
};

// Convert lbs to kg
const convertLbsToKg = (lbs: number): number => {
  return Math.round(lbs / 2.20462 * 100) / 100; // Round to 2 decimal places
};

const SetTracker: React.FC<SetTrackerProps> = ({ sets, onUpdateSet, targetReps, exercise, userProgress, currentWeek, currentDay }) => {
  const [currentSetIndex, setCurrentSetIndex] = useState(() => {
    // Find the first incomplete set
    const incompleteIndex = sets.findIndex(set => !set.completed);
    return incompleteIndex !== -1 ? incompleteIndex : 0;
  });

  const [weight, setWeight] = useState<string>('');
  const [reps, setReps] = useState<string>('');
  const [selectedRpe, setSelectedRpe] = useState<RPE | ''>('');
  const [rpeAnchorEl, setRpeAnchorEl] = useState<HTMLElement | null>(null);
  const [showCalculatedWeight, setShowCalculatedWeight] = useState<number | null>(null);
  const [rpeDialogOpen, setRpeDialogOpen] = useState(false);
  const [weightChipAnchorEl, setWeightChipAnchorEl] = useState<HTMLElement | null>(null);
  const [weightUnit, setWeightUnit] = useState<'lbs' | 'kg'>('lbs');
  const [previousDataAnchorEl, setPreviousDataAnchorEl] = useState<HTMLElement | null>(null);

  // Rest timer state
  const [restStartTime, setRestStartTime] = useState<number | null>(null);
  const [restTimer, setRestTimer] = useState(0);
  const [isManualSetSwitch, setIsManualSetSwitch] = useState(false);

  // Load rest timer from localStorage on component mount
  useEffect(() => {
    const loadRestTimerFromStorage = () => {
      try {
        const savedStartTime = localStorage.getItem('rest_timer_start');
        const savedExerciseName = localStorage.getItem('rest_timer_exercise');
        
        // Only restore if it's for the same exercise
        if (savedStartTime && savedExerciseName === exercise.exercise) {
          const startTime = parseInt(savedStartTime);
          const now = Date.now();
          const elapsed = Math.floor((now - startTime) / 1000);
          
          // Only restore if the elapsed time is reasonable (less than 30 minutes)
          if (elapsed > 0 && elapsed < 1800) {
            setRestStartTime(startTime);
            setRestTimer(elapsed);
          } else {
            localStorage.removeItem('rest_timer_start');
            localStorage.removeItem('rest_timer_exercise');
          }
        }
      } catch (err) {
        console.warn('Failed to load rest timer from localStorage:', err);
      }
    };

    loadRestTimerFromStorage();
  }, [exercise.exercise]);

  // Save rest timer to localStorage
  const saveRestTimerToStorage = useCallback((startTime: number | null) => {
    try {
      if (startTime !== null) {
        localStorage.setItem('rest_timer_start', startTime.toString());
        localStorage.setItem('rest_timer_exercise', exercise.exercise);
      } else {
        localStorage.removeItem('rest_timer_start');
        localStorage.removeItem('rest_timer_exercise');
      }
    } catch (err) {
      console.warn('Failed to save rest timer to localStorage:', err);
    }
  }, [exercise.exercise]);

  // Update rest timer every second
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (restStartTime !== null) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - restStartTime) / 1000);
        setRestTimer(elapsed);
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [restStartTime]);

  // Reset timer on component mount for fresh exercise sessions
  useEffect(() => {
    // Don't reset on mount - let localStorage handle restoration
  }, [exercise.exercise]);

  // Helper function to format rest timer in mm:ss format
  const formatRestTimer = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const currentSet = sets[currentSetIndex];

  // Get previous week data for the current set using WorkoutUtils
  const previousWeekData = useMemo(() => {
    if (!currentSet) return { weight: 0, reps: 0, hasData: false, exerciseExists: false };
    
    const data = WorkoutUtils.getPreviousWeekExerciseData(
      userProgress,
      currentWeek,
      currentDay,
      exercise.exercise,
      currentSet.setNumber,
      currentSet.type
    );
    
    // Check if the exercise exists at all in previous week
    const previousWeek = currentWeek - 1;
    if (previousWeek < 1 || !userProgress?.workoutHistory) {
      return { ...data, exerciseExists: false };
    }
    
    const previousSession = userProgress.workoutHistory.find(session => 
      session.week === previousWeek && 
      session.day === currentDay &&
      session.completed
    );
    
    if (!previousSession) {
      return { ...data, exerciseExists: false };
    }
    
    const exerciseRecord = previousSession.exercises.find(ex => 
      ex.exerciseName === exercise.exercise
    );
    
    const exerciseExists = !!exerciseRecord;
    
    return { ...data, exerciseExists };
  }, [userProgress, currentWeek, currentDay, exercise.exercise, currentSet]);

  // Get previous week's last working set weight for percentage calculations
  const previousWeekLastWorkingSetWeight = useMemo(() => {
    if (!userProgress?.workoutHistory) return null;
    
    // Find previous week's workout session for this exercise
    const previousWeek = currentWeek - 1;
    if (previousWeek < 1) return null;
    
    const previousSession = userProgress.workoutHistory.find(session => 
      session.week === previousWeek && 
      session.day === currentDay &&
      session.completed
    );
    
    if (!previousSession) return null;
    
    // Find this exercise in the previous session
    const exerciseRecord = previousSession.exercises.find(ex => 
      ex.exerciseName === exercise.exercise
    );
    
    if (!exerciseRecord) return null;
    
    // Find the last working set
    const workingSets = exerciseRecord.sets.filter(set => set.setType === 'working');
    if (workingSets.length === 0) return null;
    
    const lastWorkingSet = workingSets[workingSets.length - 1];
    return lastWorkingSet.weight;
  }, [userProgress, currentWeek, currentDay, exercise.exercise]);

  // Auto-populate fields when clicking on completed sets
  useEffect(() => {
    if (currentSet?.completed && currentSet.weight !== null && currentSet.reps !== null) {
      setWeight(currentSet.weight.toString());
      setReps(currentSet.reps.toString());
      setSelectedRpe(currentSet.actualRpe || '');
      // Set unit to lbs since stored weight is always in lbs
      setWeightUnit('lbs');
    } else {
      // Clear form fields when switching to an incomplete set
      setWeight('');
      setReps('');
      setSelectedRpe('');
    }
    setShowCalculatedWeight(null);
    
    // Reset the manual switch flag
    setIsManualSetSwitch(false);
  }, [currentSet]);

  // Calculate the weight expression in real-time
  const calculatedWeight = useMemo(() => {
    if (!weight) return null;
    
    // If it's just a number, return it
    const simpleNumber = parseFloat(weight);
    if (!isNaN(simpleNumber) && weight === simpleNumber.toString()) {
      return simpleNumber;
    }
    
    // If it contains operators, calculate the expression
    if (/[+\-*/]/.test(weight)) {
      return calculateExpression(weight);
    }
    
    return null;
  }, [weight]);

  // Show calculated result when there's a valid expression
  const displayCalculatedWeight = calculatedWeight !== null && weight.includes('+') || weight.includes('-') || weight.includes('*') || weight.includes('/');

  const handleWeightChange = (value: string) => {
    setWeight(value);
    
    // If the expression is valid and contains operators, show the calculated result
    if (/[+\-*/]/.test(value)) {
      const calculated = calculateExpression(value);
      setShowCalculatedWeight(calculated);
    } else {
      setShowCalculatedWeight(null);
    }
  };

  const handleWeightBlur = () => {
    // Auto-calculate when leaving the field
    const calculated = calculateExpression(weight);
    if (calculated !== null && /[+\-*/]/.test(weight)) {
      setWeight(calculated.toString());
      setShowCalculatedWeight(null);
    }
  };

  const handleWeightKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      // Auto-calculate when pressing Enter
      const calculated = calculateExpression(weight);
      if (calculated !== null && /[+\-*/]/.test(weight)) {
        setWeight(calculated.toString());
        setShowCalculatedWeight(null);
      }
      // Move focus to reps field
      const repsField = document.querySelector('input[placeholder="0"]') as HTMLInputElement;
      if (repsField) {
        repsField.focus();
      }
    }
  };

  // Memoize expensive calculations
  const getWarmupInfo = useCallback((setIndex: number) => {
    const warmupSets = sets.filter(set => set.type === 'warmup');
    const totalWarmupSets = warmupSets.length;
    const warmupSetIndex = warmupSets.findIndex((_, index) => 
      sets.findIndex(s => s === warmupSets[index]) === setIndex
    );
    
    if (warmupSetIndex !== -1) {
      return WorkoutUtils.getWarmupSetInfo(totalWarmupSets, warmupSetIndex);
    }
    return null;
  }, [sets]);

  // Get RPE for a working set
  const getRPEForWorkingSet = useCallback((setIndex: number) => {
    const workingSets = sets.filter(set => set.type === 'working');
    const workingSetIndex = workingSets.findIndex((_, index) => 
      sets.findIndex(s => s === workingSets[index]) === setIndex
    );
    
    if (workingSetIndex !== -1) {
      // If this is the last working set, use target RPE (last_set_rpe)
      // Otherwise use early sets RPE
      const isLastWorkingSet = workingSetIndex === workingSets.length - 1;
      return isLastWorkingSet ? exercise.last_set_rpe : exercise.early_set_rpe;
    }
    return null;
  }, [sets, exercise.last_set_rpe, exercise.early_set_rpe]);

  const currentWarmupInfo = useMemo(() => 
    currentSet?.type === 'warmup' ? getWarmupInfo(currentSetIndex) : null,
    [currentSet, currentSetIndex, getWarmupInfo]
  );
  
  const currentRPE = useMemo(() => 
    currentSet?.type === 'working' ? getRPEForWorkingSet(currentSetIndex) : null,
    [currentSet, currentSetIndex, getRPEForWorkingSet]
  );

  const handleSaveSet = useCallback(() => {
    // Use calculated weight if available, otherwise parse the weight string
    let weightNum: number;
    
    if (calculatedWeight !== null) {
      weightNum = calculatedWeight;
    } else {
      weightNum = parseFloat(weight);
    }
    
    // Convert kg to lbs if needed
    if (weightUnit === 'kg') {
      weightNum = convertKgToLbs(weightNum);
    }
    
    const repsNum = parseInt(reps);

    if (isNaN(weightNum) || isNaN(repsNum) || weightNum <= 0 || repsNum <= 0) {
      alert('Please enter valid weight and reps');
      return;
    }

    // Mark this set as completed
    onUpdateSet(currentSetIndex, weightNum, repsNum, selectedRpe as RPE);
    
    // Move to next incomplete set
    const nextIncompleteIndex = sets.findIndex((set, index) => 
      index > currentSetIndex && !set.completed
    );
    
    if (nextIncompleteIndex !== -1) {
      // Start rest timer immediately when we save a set (not first set)
      if (currentSetIndex >= 0) {
        const newStartTime = Date.now();
        setRestStartTime(newStartTime);
        setRestTimer(0);
        saveRestTimerToStorage(newStartTime);
      }
      
      // Clear form fields and move to next set
      setWeight('');
      setReps('');
      setSelectedRpe('');
      setShowCalculatedWeight(null);
      setCurrentSetIndex(nextIncompleteIndex);
    } else {
      // All sets completed - stop timer
      setWeight('');
      setReps('');
      setSelectedRpe('');
      setShowCalculatedWeight(null);
      setRestStartTime(null);
      setRestTimer(0);
      saveRestTimerToStorage(null);
    }
  }, [weight, reps, currentSetIndex, onUpdateSet, sets, calculatedWeight, selectedRpe, saveRestTimerToStorage, weightUnit]);

  const allSetsCompleted = useMemo(() => 
    sets.every(set => set.completed),
    [sets]
  );

  const handleRpeClick = (event: React.MouseEvent<HTMLElement>) => {
    setRpeAnchorEl(event.currentTarget);
  };

  const handleRpeClose = () => {
    setRpeAnchorEl(null);
  };

  const handleRpeDialogOpen = () => {
    setRpeDialogOpen(true);
  };

  const handleRpeDialogClose = () => {
    setRpeDialogOpen(false);
  };

  const handleWeightChipClick = (event: React.MouseEvent<HTMLElement>) => {
    setWeightChipAnchorEl(event.currentTarget);
  };

  const handleWeightChipClose = () => {
    setWeightChipAnchorEl(null);
  };

  const handlePreviousDataClick = (event: React.MouseEvent<HTMLElement>) => {
    setPreviousDataAnchorEl(event.currentTarget);
  };

  const handlePreviousDataClose = () => {
    setPreviousDataAnchorEl(null);
  };

  const rpePopoverOpen = Boolean(rpeAnchorEl);
  const weightChipPopoverOpen = Boolean(weightChipAnchorEl);
  const previousDataPopoverOpen = Boolean(previousDataAnchorEl);

  // Get all RPE options with descriptions for the dialog
  const rpeOptions = Object.values(RPE).filter(rpe => rpe !== RPE.NONE).map(rpe => ({
    value: rpe,
    label: formatRPEForDisplay(rpe),
    description: getRPEDescription(rpe)
  }));

  // Calculate suggested weight based on percentage and previous week's last working set
  const calculateSuggestedWeight = (percentage: string) => {
    if (!previousWeekLastWorkingSetWeight) return null;
    
    // Extract percentage number (e.g., "50%" -> 50)
    const percentMatch = percentage.match(/(\d+)%/);
    if (!percentMatch) return null;
    
    const percent = parseInt(percentMatch[1]);
    const suggestedWeight = (previousWeekLastWorkingSetWeight * percent) / 100;
    
    return Math.round(suggestedWeight * 100) / 100; // Round to 2 decimal places
  };

  return (
    <Card sx={{ overflow: 'visible' }}>
      <CardContent sx={{ overflow: 'visible' }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Track Your Sets
        </Typography>
        
        {/* Sets Overview - Responsive Grid */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: `repeat(${sets.length}, 1fr)`, 
            gap: 1.5,
            maxWidth: '100%'
          }}>
            {sets.map((set, index) => {
              const setWarmupInfo = set.type === 'warmup' ? getWarmupInfo(index) : null;
              
              return (
                <Card
                  key={index}
                  variant="outlined"
                  onClick={() => {
                    setIsManualSetSwitch(true);
                    setCurrentSetIndex(index);
                  }}
                  sx={{
                    height: 75,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    bgcolor: set.completed 
                      ? 'success.light' 
                      : index === currentSetIndex 
                        ? 'primary.light' 
                        : 'background.paper',
                    borderColor: set.completed 
                      ? 'success.main' 
                      : index === currentSetIndex 
                        ? 'primary.main' 
                        : 'divider',
                    borderWidth: index === currentSetIndex ? 2 : 1,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 2,
                    }
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    p: 1,
                    '&:last-child': { pb: 1 }
                  }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                      {set.type === 'warmup' ? 'W' : 'S'}{set.setNumber}
                    </Typography>
                    {set.completed ? (
                      <Box sx={{ textAlign: 'center' }}>
                        <CheckCircle sx={{ fontSize: 20, mb: 0.5, color: 'success.main' }} />
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center' }}>
                        <Box 
                          sx={{ 
                            width: 20, 
                            height: 20, 
                            borderRadius: 2, 
                            border: 2, 
                            borderColor: 'divider',
                            mb: 0.5,
                            mx: 'auto'
                          }} 
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>

        {!allSetsCompleted && currentSet && (
          <Card variant="outlined" sx={{ bgcolor: 'background.paper', overflow: 'visible' }}>
            <CardContent sx={{ overflow: 'visible' }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {currentSet.type === 'warmup' ? 'Warmup' : 'Working'} Set {currentSet.setNumber}
                </Typography>
                
                {/* Display appropriate target based on set type */}
                {currentSet.type === 'warmup' && currentWarmupInfo ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1.5, 
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Chip 
                      label={`${currentWarmupInfo.reps} reps target`} 
                      color="warning" 
                      size="medium"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '.8rem' },
                        height: { xs: 36, sm: 40 },
                        fontWeight: 'bold',
                        '& .MuiChip-label': { 
                          px: { xs: 2, sm: 2.5 },
                          fontSize: 'inherit',
                          fontWeight: 'inherit'
                        }
                      }}
                    />
                    <Chip 
                      label={`${currentWarmupInfo.weightPercentage} working weight`} 
                      color="info" 
                      size="medium"
                      variant="outlined"
                      onClick={handleWeightChipClick}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '.8rem' },
                        height: { xs: 36, sm: 40 },
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '& .MuiChip-label': { 
                          px: { xs: 2, sm: 2.5 },
                          fontSize: 'inherit',
                          fontWeight: 'inherit'
                        }
                      }}
                    />
                    <Chip 
                      label={`${exercise.rest} rest`} 
                      color="secondary" 
                      size="medium"
                      variant="outlined"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '.8rem' },
                        height: { xs: 36, sm: 40 },
                        fontWeight: 'bold',
                        '& .MuiChip-label': { 
                          px: { xs: 2, sm: 2.5 },
                          fontSize: 'inherit',
                          fontWeight: 'inherit'
                        }
                      }}
                    />
                    {/* Rest Timer Chip - Only show if not the first set and timer is active */}
                    {(() => {
                      const shouldShow = restStartTime !== null && currentSetIndex > 0;
                      return shouldShow;
                    })() && (
                      <Chip 
                        label={`${formatRestTimer(restTimer)} resting`} 
                        color="primary" 
                        size="medium"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '.8rem' },
                          height: { xs: 36, sm: 40 },
                          fontWeight: 'bold',
                          '& .MuiChip-label': { 
                            px: { xs: 2, sm: 2.5 },
                            fontSize: 'inherit',
                            fontWeight: 'inherit'
                          }
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1.5, 
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Chip 
                      label={`${targetReps} reps target`} 
                      color="warning" 
                      size="medium"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '.8rem' },
                        height: { xs: 36, sm: 40 },
                        fontWeight: 'bold',
                        '& .MuiChip-label': { 
                          px: { xs: 2, sm: 2.5 },
                          fontSize: 'inherit',
                          fontWeight: 'inherit'
                        }
                      }}
                    />
                    <Chip 
                      label={"100% working weight"} 
                      color="info" 
                      size="medium"
                      variant="outlined"
                      onClick={handleWeightChipClick}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '.8rem' },
                        height: { xs: 36, sm: 40 },
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '& .MuiChip-label': { 
                          px: { xs: 2, sm: 2.5 },
                          fontSize: 'inherit',
                          fontWeight: 'inherit'
                        }
                      }}
                    />
                    <Chip 
                      label={`${exercise.rest} rest`} 
                      color="secondary" 
                      size="medium"
                      variant="outlined"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '.8rem' },
                        height: { xs: 36, sm: 40 },
                        fontWeight: 'bold',
                        '& .MuiChip-label': { 
                          px: { xs: 2, sm: 2.5 },
                          fontSize: 'inherit',
                          fontWeight: 'inherit'
                        }
                      }}
                    />
                    {/* Rest Timer Chip - Only show if not the first set and timer is active */}
                    {(() => {
                      const shouldShow = restStartTime !== null && currentSetIndex > 0;
                      return shouldShow;
                    })() && (
                      <Chip 
                        label={`${formatRestTimer(restTimer)} resting`} 
                        color="primary" 
                        size="medium"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '.8rem' },
                          height: { xs: 36, sm: 40 },
                          fontWeight: 'bold',
                          '& .MuiChip-label': { 
                            px: { xs: 2, sm: 2.5 },
                            fontSize: 'inherit',
                            fontWeight: 'inherit'
                          }
                        }}
                      />
                    )}
                    {currentRPE && (
                      <>
                        <Chip 
                          label={formatRPEForDisplay(currentRPE)} 
                          color="success" 
                          size="medium"
                          variant="outlined"
                          onClick={handleRpeClick}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '.8rem' },
                            height: { xs: 36, sm: 40 },
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            '& .MuiChip-label': { 
                              px: { xs: 2, sm: 2.5 },
                              fontSize: 'inherit',
                              fontWeight: 'inherit'
                            }
                          }}
                        />
                        <Popover
                          open={rpePopoverOpen}
                          anchorEl={rpeAnchorEl}
                          onClose={handleRpeClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                          }}
                          slotProps={{
                            paper: {
                              sx: {
                                maxWidth: 280,
                                borderRadius: 2,
                                boxShadow: 3,
                                mx: 2, // Horizontal margin to prevent edge collision
                              }
                            }
                          }}
                          marginThreshold={16}
                          disableScrollLock={true}
                        >
                          <Typography sx={{ p: 2, fontSize: '0.9rem', lineHeight: 1.4 }}>
                            {getRPEDescription(currentRPE)}
                          </Typography>
                        </Popover>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              {/* Weight Chip Popover */}
              <Popover
                open={weightChipPopoverOpen}
                anchorEl={weightChipAnchorEl}
                onClose={handleWeightChipClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                slotProps={{
                  paper: {
                    sx: {
                      maxWidth: 280,
                      borderRadius: 2,
                      boxShadow: 3,
                      mx: 2, // Horizontal margin to prevent edge collision
                    }
                  }
                }}
                marginThreshold={16}
                disableScrollLock={true}
              >
                <Box sx={{ p: 2 }}>
                  {(() => {
                    // Determine which percentage to show based on current set type
                    let percentage = "100%";
                    if (currentSet?.type === 'warmup' && currentWarmupInfo) {
                      percentage = currentWarmupInfo.weightPercentage;
                    }
                    
                    const suggestedWeight = calculateSuggestedWeight(percentage);
                    
                    if (suggestedWeight !== null && previousWeekLastWorkingSetWeight !== null) {
                      return (
                        <Box>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Suggested Weight ({percentage})
                          </Typography>
                          <Typography variant="body1" color="primary" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                            {suggestedWeight} lbs
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.8rem' }}>
                            Based on last week's final working set: {previousWeekLastWorkingSetWeight} lbs
                          </Typography>
                        </Box>
                      );
                    } else {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          No previous week data available for weight calculation
                        </Typography>
                      );
                    }
                  })()}
                </Box>
              </Popover>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, overflow: 'visible' }}>
                {/* Previous week data display - Above inputs */}
                {previousWeekData.exerciseExists && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    gap: 3,
                    py: 1,
                    px: 2,
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    {previousWeekData.hasData ? (
                      <>
                        <Typography 
                          variant="caption" 
                          onClick={handlePreviousDataClick}
                          sx={{ 
                            color: 'text.secondary', 
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                            fontWeight: 'medium',
                            cursor: 'pointer',
                            '&:hover': {
                              color: 'primary.main',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          Previous: {previousWeekData.weight} lbs
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary', 
                          fontSize: '0.8rem',
                          fontStyle: 'italic',
                          fontWeight: 'medium'
                        }}>
                          {previousWeekData.reps} reps
                        </Typography>
                        {/* Show RPE for working sets only */}
                        {currentSet?.type === 'working' && previousWeekData.rpe && (
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary', 
                            fontSize: '0.8rem',
                            fontStyle: 'italic',
                            fontWeight: 'medium'
                          }}>
                            {formatRPEForDisplay(previousWeekData.rpe)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '0.8rem',
                        fontStyle: 'italic',
                        fontWeight: 'medium'
                      }}>
                        No previous data to compare
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Previous Data Popover */}
                {previousWeekData.hasData && (
                  <Popover
                    open={previousDataPopoverOpen}
                    anchorEl={previousDataAnchorEl}
                    onClose={handlePreviousDataClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          maxWidth: 200,
                          borderRadius: 2,
                          boxShadow: 3,
                          mx: 2,
                        }
                      }
                    }}
                    marginThreshold={16}
                    disableScrollLock={true}
                  >
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        Previous Weight
                      </Typography>
                      <Typography variant="body1" color="primary" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                        {previousWeekData.weight} lbs
                      </Typography>
                      <Typography variant="body1" color="info.main" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {convertLbsToKg(previousWeekData.weight)} kg
                      </Typography>
                    </Box>
                  </Popover>
                )}

                {/* Weight Input Row with Unit Toggle */}
                <Box sx={{ display: 'flex', gap: 2, overflow: 'visible' }}>
                  <TextField
                    label={`Weight (${weightUnit})`}
                    type="text"
                    value={weight}
                    onChange={(e) => handleWeightChange(e.target.value)}
                    onBlur={handleWeightBlur}
                    onKeyDown={handleWeightKeyPress}
                    placeholder={weightUnit === 'lbs' ? "e.g. 45+25+25 or 135" : "e.g. 20+11+11 or 61"}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      '& .MuiInputLabel-root': {
                        zIndex: 1,
                        position: 'absolute',
                        top: '50%',
                        left: '14px',
                        transform: 'translateY(-50%)',
                        transition: 'all 0.2s ease-in-out',
                        pointerEvents: 'none',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                          top: '0px',
                          transform: 'translate(14px, -9px) scale(0.75)',
                          backgroundColor: 'rgba(31, 41, 55, 0.95)',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          zIndex: 2,
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        position: 'relative',
                        overflow: 'visible',
                        '& fieldset': {
                          borderRadius: 3,
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '16.5px 14px',
                      },
                    }}
                  />
                  
                  {/* Unit Toggle */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 80,
                    gap: 0.5
                  }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Unit
                    </Typography>
                    <Box sx={{ 
                      display: 'flex',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper'
                    }}>
                      <Button
                        size="small"
                        onClick={() => setWeightUnit('lbs')}
                        sx={{
                          minWidth: 35,
                          px: 1,
                          py: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          borderRadius: 0,
                          bgcolor: weightUnit === 'lbs' ? 'info.main' : 'transparent',
                          color: weightUnit === 'lbs' ? 'info.contrastText' : 'text.secondary',
                          '&:hover': {
                            bgcolor: weightUnit === 'lbs' ? 'info.dark' : 'action.hover',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        lbs
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setWeightUnit('kg')}
                        sx={{
                          minWidth: 35,
                          px: 1,
                          py: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          borderRadius: 0,
                          bgcolor: weightUnit === 'kg' ? 'info.main' : 'transparent',
                          color: weightUnit === 'kg' ? 'info.contrastText' : 'text.secondary',
                          '&:hover': {
                            bgcolor: weightUnit === 'kg' ? 'info.dark' : 'action.hover',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        kg
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {/* Reps and RPE Row */}
                <Box sx={{ display: 'flex', gap: 2, overflow: 'visible' }}>
                  <TextField
                    label="Reps"
                    type="number"
                    value={reps}
                    onChange={(e) => setReps(e.target.value)}
                    placeholder="0"
                    inputProps={{ min: "1" }}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      '& .MuiInputLabel-root': {
                        zIndex: 1,
                        position: 'absolute',
                        top: '50%',
                        left: '14px',
                        transform: 'translateY(-50%)',
                        transition: 'all 0.2s ease-in-out',
                        pointerEvents: 'none',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                          top: '0px',
                          transform: 'translate(14px, -9px) scale(0.75)',
                          backgroundColor: 'rgba(31, 41, 55, 0.95)',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          zIndex: 2,
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        position: 'relative',
                        overflow: 'visible',
                        '& fieldset': {
                          borderRadius: 3,
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '16.5px 14px',
                      },
                    }}
                  />
                  
                  {/* RPE Selection for Working Sets */}
                  {currentSet?.type === 'working' && (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControl 
                        variant="outlined" 
                        sx={{ 
                          flex: 1,
                          '& .MuiInputLabel-root': {
                            zIndex: 1,
                            position: 'absolute',
                            top: '50%',
                            left: '14px',
                            transform: 'translateY(-50%)',
                            transition: 'all 0.2s ease-in-out',
                            pointerEvents: 'none',
                            '&.Mui-focused, &.MuiFormLabel-filled': {
                              top: '0px',
                              transform: 'translate(14px, -9px) scale(0.75)',
                              backgroundColor: 'rgba(31, 41, 55, 0.95)',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              zIndex: 2,
                            },
                          },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            '& fieldset': {
                              borderRadius: 3,
                            },
                          },
                          '& .MuiSelect-select': {
                            padding: '16.5px 14px',
                          },
                        }}
                      >
                        <InputLabel>RPE</InputLabel>
                        <Select
                          value={selectedRpe}
                          onChange={(e) => setSelectedRpe(e.target.value as RPE)}
                          label="RPE"
                        >
                          <MenuItem value="">
                            <em>Select RPE</em>
                          </MenuItem>
                          {rpeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <IconButton 
                        onClick={handleRpeDialogOpen}
                        size="small" 
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Button
                  onClick={handleSaveSet}
                  disabled={!weight || !reps}
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ 
                    py: 2, 
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  Save Set
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {allSetsCompleted && (
          <Card variant="outlined" sx={{ bgcolor: 'success.light', textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'success.dark' }}>
                ✅ All sets completed!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total volume: {sets.reduce((total, set) => 
                  set.completed && set.weight && set.reps 
                    ? total + (set.weight * set.reps) 
                    : total, 0
                ).toFixed(1)} lbs
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* RPE Information Dialog */}
        <Dialog 
          open={rpeDialogOpen} 
          onClose={handleRpeDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '80vh'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              RPE (Rate of Perceived Exertion) Guide
            </Typography>
          </DialogTitle>
          <DialogContent>
            <List sx={{ py: 0 }}>
              {rpeOptions.map((option) => (
                <ListItem 
                  key={option.value}
                  sx={{ 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    {option.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {option.description}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleRpeDialogClose} variant="contained" fullWidth>
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default React.memo(SetTracker); 